import { Component, HostListener, OnInit, Renderer2, ChangeDetectionStrategy } from '@angular/core';
import { Router } from '@angular/router';

interface MenuItem {
  label: string;
  icon?: string;
  route?: string;
  exact?: boolean;
  expanded?: boolean;
  disabled?: boolean;
  children?: MenuItem[];
}

/** Como o menu se comporta em cada faixa de tela. */
export type ModoSidebar = 'mobile' | 'tablet' | 'desktop';

/** Barra inferior até 767px, menu lateral só com ícones até 1023px, menu aberto daí para cima. */
const LARGURA_TABLET = 768;
const LARGURA_DESKTOP = 1024;

@Component({
  selector: 'app-main-sidebar',
  standalone: false,
  templateUrl: './main-sidebar.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './main-sidebar.component.css'
})
export class MainSidebarComponent implements OnInit {
  modo: ModoSidebar = 'desktop';

  isCollapsed = false;
  isMobile = false;

  /** O que a pessoa escolheu no desktop, para o menu voltar assim ao sair do tablet. */
  private desktopAberto = true;
  perfilNome: string | null = 'Administrador';
  exibirMenuMaster = true;
  menuItems: MenuItem[] = [
    {
      label: 'Home',
      icon: 'house',
      route: '/adm/home',
      exact: true
    },
    {
      label: 'PNCP',
      icon: 'shield-check',
      route: '/adm/pncp/permissoesPncp',
      children: [
        {
          label: 'Permissoes',
          icon: 'key-round',
          route: '/adm/pncp/permissoesPncp',
          exact: true
        },
        {
          label: 'Dados PNCP',
          icon: 'file-text',
          route: '/adm/pncp/dados-pncp'
        },
        {
          label: 'Contratações',
          icon: 'clipboard-list',
          route: '/adm/pncp/contratacoes',
          exact: true
        },
        {
          label: 'Intenções de Registro de Preços',
          icon: 'file-signature',
          route: '/adm/pncp/intencoes-registro-preco',
          exact: true
        },
        {
          label: 'Planos de Contratação Anual',
          icon: 'calendar-range',
          route: '/adm/pncp/planos-contratacao-anual',
          exact: true
        }
      ]
    },
    {
      label: 'Licitações',
      icon: 'scale',
      route: '/adm/pncp/contratacoes',
      children: [
        {
          label: 'Lei 14.133/21',
          icon: 'file-check-2',
          route: '/adm/pncp/contratacoes',
          exact: true
        },
        {
          label: 'Lei 8.666/93',
          icon: 'file-clock',
          route: '/adm/licitacoes/home',
          exact: true
        }
      ]
    },
    {
      label: 'Certificados',
      icon: 'badge-check',
      route: '/adm/certificados/dashboard',
      children: [
        {
          label: 'Dashboard',
          icon: 'layout-dashboard',
          route: '/adm/certificados/dashboard',
          exact: true
        },
        {
          label: 'Sites ativos',
          icon: 'globe',
          route: '/adm/certificados/sites-ativos'
        },
        {
          label: 'Gerenciamento',
          icon: 'sliders-horizontal',
          route: '/adm/certificados/certificados-gerenciamento'
        }
      ]
    },
    {
      label: 'Migracao de Dados',
      icon: 'database',
      route: '/adm/migracao-dados',
      exact: true
    }
  ];
  masterMenuItems: MenuItem[] = [
    {
      label: 'Cadastro de Clientes',
      icon: 'shield',
      route: '/adm/imap/cliente'
    },
    {
      label: 'Gerenciador de Modulos',
      icon: 'book-open',
      route: '/adm/imap/modulo'
    },
    {
      label: 'Cadastro de Usuarios',
      icon: 'users',
      route: '/adm/imap/usuario'
    },
    {
      label: 'Carta de Serviços',
      icon: 'layout-grid',
      route: '/adm/carta-servicos/imap/modulo-carta-servicos'
    },
    {
      label: 'Institucional',
      icon: 'landmark',
      route: '/adm/institucional'
    }, 
  ];

  constructor(
    private readonly renderer: Renderer2,
    private readonly router: Router
  ) { }

  ngOnInit(): void {
    this.updateLayout();
  }

  /** Só o desktop deixa abrir e fechar: no tablet e no celular o menu tem forma fixa. */
  get podeAlternar(): boolean {
    return this.modo === 'desktop';
  }

  /** Rótulo ao lado do ícone: só no menu aberto do desktop. */
  get exibirRotulos(): boolean {
    return this.modo === 'desktop' && !this.isCollapsed;
  }

  /** No celular o submenu abre acima da barra, então o rótulo continua visível. */
  get exibirRotulosSubmenu(): boolean {
    return this.modo === 'mobile' || this.exibirRotulos;
  }

  @HostListener('window:resize')
  updateLayout(): void {
    const largura = window.innerWidth;

    this.modo = largura < LARGURA_TABLET
      ? 'mobile'
      : largura < LARGURA_DESKTOP
        ? 'tablet'
        : 'desktop';

    this.isMobile = this.modo !== 'desktop';

    if (this.modo === 'desktop') {
      this.isCollapsed = !this.desktopAberto;
      this.aplicarClassesDoCorpo();
      return;
    }

    // Fora do desktop o menu vive recolhido: barra inferior no celular, lateral no tablet.
    this.isCollapsed = true;
    this.aplicarClassesDoCorpo();
  }

  toggleSidebar(): void {
    if (!this.podeAlternar) {
      return;
    }

    this.isCollapsed = !this.isCollapsed;
    this.desktopAberto = !this.isCollapsed;
    this.aplicarClassesDoCorpo();
  }

  /** O conteúdo se desloca conforme a forma do menu, por isso as classes vão no body. */
  private aplicarClassesDoCorpo(): void {
    const mobile = this.modo === 'mobile';
    const recolhido = !mobile && this.isCollapsed;

    if (mobile) {
      this.renderer.addClass(document.body, 'sidebar-mobile');
    } else {
      this.renderer.removeClass(document.body, 'sidebar-mobile');
    }

    if (recolhido) {
      this.renderer.addClass(document.body, 'sidebar-collapsed');
    } else {
      this.renderer.removeClass(document.body, 'sidebar-collapsed');
    }
  }

  toggleMenuItem(item: MenuItem): void {
    if (!item.children?.length) {
      return;
    }

    item.expanded = !item.expanded;
  }

  isMenuItemActive(item: MenuItem): boolean {
    const url = this.router.url.split('?')[0];

    if (item.route) {
      const rotaAtiva = item.exact ? url === item.route : url.startsWith(item.route);
      if (rotaAtiva) {
        return true;
      }
    }

    return item.children?.some((child) => this.isMenuItemActive(child)) ?? false;
  }
}
